import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const sections = [
  { id: "overview", title: "1. Overview" },
  { id: "capital-at-risk", title: "2. Capital at Risk" },
  { id: "illiquidity", title: "3. Illiquidity Risk" },
  { id: "target-returns", title: "4. Target Returns Not Guaranteed" },
  { id: "fsma", title: "5. FSMA 2000 & Regulatory Status" },
  { id: "investor-classification", title: "6. Investor Classification" },
  { id: "fscs", title: "7. FSCS Protection" },
  { id: "tax", title: "8. Tax Considerations" },
  { id: "past-performance", title: "9. Past Performance" },
  { id: "property-risks", title: "10. Property & Sector Risks" },
  { id: "no-advice", title: "11. No Financial Advice" },
  { id: "nasdaq", title: "12. NASDAQ Dubai Products" },
  { id: "complaints", title: "13. Complaints" },
];

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-28 mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ background: "linear-gradient(to bottom, #C9A84C, #E2C27A)" }} />
        <h2 className="text-xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h2>
      </div>
      <div className="text-slate-600 text-sm leading-relaxed space-y-4 pl-4">
        {children}
      </div>
    </section>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return <p className="text-slate-600 text-sm leading-relaxed">{children}</p>;
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 mt-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-alert-line text-[#C9A84C] text-xs leading-none" />
          </div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function HighlightBox({ title, children, variant = "gold" }: { title: string; children: React.ReactNode; variant?: "gold" | "navy" | "red" }) {
  const styles = {
    gold: { bg: "rgba(201,168,76,0.07)", border: "rgba(201,168,76,0.3)", titleColor: "#C9A84C" },
    navy: { bg: "rgba(7,26,46,0.05)", border: "rgba(7,26,46,0.15)", titleColor: "#071A2E" },
    red: { bg: "rgba(239,68,68,0.05)", border: "rgba(239,68,68,0.2)", titleColor: "#dc2626" },
  };
  const style = styles[variant];

  return (
    <div className="rounded-xl px-5 py-5 my-4" style={{ background: style.bg, border: `1px solid ${style.border}` }}>
      <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: style.titleColor }}>{title}</p>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

export default function RiskDisclaimerPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      <Navbar />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 300,
          background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 60%, #071A2E 100%)",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E2C27A 50%, #C9A84C 70%, transparent)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent)" }} />
        <div className="absolute top-[-60px] right-[-60px] w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-14">
          <div className="flex items-center gap-2 text-xs text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line leading-none" />
            <span className="text-white/70">Risk Disclaimer</span>
          </div>
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
            <i className="ri-shield-check-line text-xs leading-none" />
            Section 21 FSMA 2000 Compliant
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Risk Disclaimer
          </h1>
          <p className="text-white/55 text-sm max-w-xl leading-relaxed">
            Important risk information relating to all investment products offered by Thorpe Dellow. Please read this document carefully before making any investment decision.
          </p>
          <p className="text-white/35 text-xs mt-4">Last updated: 28 March 2026 &nbsp;·&nbsp; Version 1.0</p>
        </div>
      </section>

      {/* Prominent risk banner */}
      <div style={{ background: "#C9A84C" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-fill text-[#071A2E] text-base leading-none" />
            </div>
            <p className="text-[#071A2E] text-xs font-semibold leading-relaxed">
              <strong>Important:</strong> Investments offered by Thorpe Dellow are speculative and involve a significant degree of risk. Your capital is at risk and you may lose some or all of your investment. These products are not regulated by the Financial Conduct Authority (FCA) and are not covered by the Financial Services Compensation Scheme (FSCS). Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

          {/* Sidebar TOC */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white rounded-2xl p-6 border border-slate-100">
              <p className="text-[10px] font-bold text-[#C9A84C] uppercase tracking-widest mb-4">Contents</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left text-xs text-slate-500 hover:text-[#071A2E] py-1.5 px-2 rounded-lg hover:bg-[#EEF2F9] transition-colors cursor-pointer"
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10">

            <Section id="overview" title="1. Overview">
              <Para>
                This Risk Disclaimer forms part of the information provided by Thorpe Dellow in connection with all investment products and financial promotions issued by or on behalf of Thorpe Dellow. It should be read in conjunction with the relevant Information Memorandum and any other product documentation.
              </Para>
              <Para>
                All financial promotions are issued in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA 2000) and are directed solely at qualifying investors. By accessing investment materials, you confirm you are a qualifying investor.
              </Para>
            </Section>

            <Section id="capital-at-risk" title="2. Capital at Risk">
              <HighlightBox title="Key Risk Warning" variant="red">
                <p><strong>Your capital is at risk. You may lose some or all of the money you invest.</strong> Thorpe Dellow investment products are not deposit accounts and are not protected by any government guarantee scheme.</p>
              </HighlightBox>
              <Para>Risks to your invested capital include:</Para>
              <Ul items={[
                "Default or insolvency of the underlying borrower or Special Purpose Vehicle (SPV)",
                "A decline in the value of the underlying property assets",
                "A failure of the housing association or operator to meet their lease obligations",
                "Deterioration in government social housing funding or regulatory changes affecting rental income",
                "Enforcement and recovery costs reducing net proceeds available to investors",
              ]} />
              <Para>
                While investor capital is secured by first legal charges over the underlying properties, the realisable value of those properties on enforcement may be lower than the principal invested, particularly in adverse market conditions.
              </Para>
            </Section>

            <Section id="illiquidity" title="3. Illiquidity Risk">
              <Para>
                Investments in Thorpe Dellow products are <strong className="text-[#071A2E]">illiquid</strong>. There is no established secondary market for the transfer or sale of these investment instruments. You should not invest any funds that you may require access to before the end of the stated investment term.
              </Para>
              <Para>
                Early redemption may not be available, and where it is available, it may be subject to penalties, discounts, or delays. Thorpe Dellow does not guarantee early redemption under any circumstances.
              </Para>
            </Section>

            <Section id="target-returns" title="4. Target Returns Not Guaranteed">
              <HighlightBox title="Important" variant="gold">
                <p>All stated returns — including the 10.00% per annum target return — are illustrative targets only. They are based on assumptions about underlying property performance, lease income, and market conditions, and are <strong>not guaranteed</strong>.</p>
              </HighlightBox>
              <Para>Actual returns may be lower than the target, and in adverse scenarios, no return may be achieved at all. Factors that could affect returns include:</Para>
              <Ul items={[
                "Vacancy or voidage in the underlying properties",
                "Housing association default or insolvency",
                "Changes in government social housing policy",
                "Unexpected maintenance or regulatory costs",
                "Movements in UK interest rates or credit markets",
                "Force majeure events outside the control of the manager",
              ]} />
            </Section>

            <Section id="fsma" title="5. FSMA 2000 & Regulatory Status">
              <Para>
                All financial promotions issued by Thorpe Dellow are communicated in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA 2000). This means that the content of financial promotions has been approved by a person authorised under FSMA 2000.
              </Para>
              <Para>
                Thorpe Dellow&apos;s investment products constitute <strong className="text-[#071A2E]">non-mainstream pooled investments (NMPIs)</strong> or unregulated collective investment schemes. As such, they may only be promoted to certain categories of investors as set out in Section 6 below.
              </Para>
              <Para>
                These investments are <strong className="text-[#071A2E]">not regulated by the Financial Conduct Authority (FCA)</strong> for the purposes of investment management or dealing in investments. Thorpe Dellow is not an FCA-authorised firm.
              </Para>
            </Section>

            <Section id="investor-classification" title="6. Investor Classification">
              <Para>Access to Thorpe Dellow investment materials is restricted to the following categories of investor:</Para>
              <div className="space-y-3 mt-3">
                {[
                  {
                    type: "Certified High Net Worth Individual",
                    desc: "An individual with annual net income of £100,000 or more, or net assets of £250,000 or more (excluding primary residence and pension). Certification must be signed within the previous 12 months.",
                    ref: "FSMA 2000 (Financial Promotion) Order 2005, Article 48",
                  },
                  {
                    type: "Self-Certified Sophisticated Investor",
                    desc: "An individual who meets one or more of the criteria under Article 50A — for example, having made two or more investments in an unlisted company, or being a director of a company with a turnover of at least £1 million.",
                    ref: "FSMA 2000 (Financial Promotion) Order 2005, Article 50A",
                  },
                  {
                    type: "Eligible Retail Investor",
                    desc: "Subject to an appropriateness assessment to confirm they understand the risks involved in investing in speculative, illiquid securities.",
                    ref: "FSMA 2000 (Financial Promotion) Order 2005, Article 4(2)",
                  },
                ].map((cat) => (
                  <div key={cat.type} className="bg-[#EEF2F9] rounded-xl p-4">
                    <p className="text-xs font-bold text-[#071A2E] mb-1">{cat.type}</p>
                    <p className="text-xs text-slate-500 leading-relaxed mb-1">{cat.desc}</p>
                    <p className="text-[10px] text-[#C9A84C] font-medium">{cat.ref}</p>
                  </div>
                ))}
              </div>
              <Para>
                If you do not meet any of the above criteria, you must not access or rely on investment materials issued by Thorpe Dellow.
              </Para>
            </Section>

            <Section id="fscs" title="7. FSCS Protection">
              <HighlightBox title="Not FSCS Protected" variant="red">
                <p>Investments in Thorpe Dellow products are <strong>not covered by the Financial Services Compensation Scheme (FSCS)</strong>. If Thorpe Dellow or the SPV becomes insolvent, you will not be entitled to compensation from the FSCS.</p>
              </HighlightBox>
              <Para>
                The FSCS protects deposits held with FCA-authorised banks and building societies, and certain investments made through FCA-regulated firms. Thorpe Dellow&apos;s products do not fall within either category.
              </Para>
            </Section>

            <Section id="tax" title="8. Tax Considerations">
              <Para>
                The tax treatment of your investment depends on your individual circumstances and may be subject to change in the future. The following general points apply:
              </Para>
              <Ul items={[
                "Interest income distributions may be subject to UK Income Tax at your marginal rate",
                "Capital gains on disposal may be subject to Capital Gains Tax (CGT)",
                "Holding these investments within an ISA or SIPP may not be permitted — you should confirm eligibility with your provider",
                "Non-UK residents may be subject to withholding taxes depending on their country of residence",
              ]} />
              <Para>
                <strong className="text-[#071A2E]">Thorpe Dellow does not provide tax advice.</strong> You should seek independent tax advice specific to your circumstances before investing.
              </Para>
            </Section>

            <Section id="past-performance" title="9. Past Performance">
              <HighlightBox title="Standard Warning" variant="gold">
                <p>Past performance is not a reliable indicator of future results. Historical returns or case studies referenced in any Thorpe Dellow materials should not be taken as a guarantee or indication of future performance.</p>
              </HighlightBox>
              <Para>
                Economic conditions, property market dynamics, and social housing policy may change materially from the period represented by any historical data. Prospective investors should not place undue reliance on past performance figures.
              </Para>
            </Section>

            <Section id="property-risks" title="10. Property & Sector Risks">
              <Para>In addition to the general investment risks described above, investments backed by UK social housing property assets are subject to specific sector risks, including:</Para>
              <Ul items={[
                "Changes to government housing benefit policy or social housing funding that reduce rental income",
                "Regulatory changes affecting the operation of assisted living or supported housing facilities",
                "CQC (Care Quality Commission) compliance failures that result in loss of operating licences",
                "Property market downturns that reduce the value of underlying assets on enforcement",
                "Environmental or structural issues affecting the condition or habitability of properties",
                "Changes in local planning or zoning regulations",
              ]} />
            </Section>

            <Section id="no-advice" title="11. No Financial Advice">
              <Para>
                Nothing on the Thorpe Dellow website, in any Information Memorandum, in any marketing material, or in any communication from our team constitutes <strong className="text-[#071A2E]">personalised financial advice</strong>.
              </Para>
              <Para>
                Thorpe Dellow strongly recommends that all prospective investors seek independent financial and legal advice from a suitably qualified adviser before making any investment decision. You should satisfy yourself that any investment you make is appropriate for your financial situation, investment objectives, and risk tolerance.
              </Para>
            </Section>

            <Section id="nasdaq" title="12. NASDAQ Dubai Products">
              <Para>
                Where Thorpe Dellow offers products listed on NASDAQ Dubai, additional considerations apply:
              </Para>
              <Ul items={[
                "NASDAQ Dubai-listed securities are subject to the rules and regulations of the Dubai Financial Services Authority (DFSA) and NASDAQ Dubai Exchange",
                "Investors in NASDAQ Dubai products may face additional regulatory, currency, and jurisdictional risks",
                "Tax treatment of NASDAQ Dubai instruments may differ from UK-domiciled products",
                "You should obtain specific legal and tax advice if you are considering investing in any NASDAQ Dubai-listed product",
              ]} />
            </Section>

            <Section id="complaints" title="13. Complaints">
              <Para>
                If you have a complaint about any aspect of Thorpe Dellow&apos;s services or financial promotions, please contact us in the first instance:
              </Para>
              <div className="bg-[#071A2E] rounded-2xl p-6 mt-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { icon: "ri-mail-line", label: "Email", value: "complaints@thorpedellow.com", href: "mailto:complaints@thorpedellow.com" },
                    { icon: "ri-map-pin-2-line", label: "Post", value: "Compliance, One Embankment, Leeds LS1 4DW, United Kingdom" },
                  ].map((c) => (
                    <div key={c.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex-shrink-0">
                        <i className={`${c.icon} text-[#C9A84C] text-sm leading-none`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">{c.label}</p>
                        {c.href ? (
                          <a href={c.href} className="text-white text-xs font-medium hover:text-[#C9A84C] transition-colors">{c.value}</a>
                        ) : (
                          <p className="text-white text-xs font-medium">{c.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Para>
                We will acknowledge your complaint within 5 business days and provide a full written response within 28 days. If you remain dissatisfied and your complaint relates to an FCA-regulated activity, you may have the right to refer it to the Financial Ombudsman Service at <a href="https://www.financial-ombudsman.org.uk" target="_blank" rel="nofollow noreferrer" className="text-[#C9A84C] hover:underline">financial-ombudsman.org.uk</a>.
              </Para>
            </Section>

            {/* Bottom nav */}
            <div className="border-t border-slate-100 pt-8 flex flex-col sm:flex-row gap-3 justify-between items-center">
              <p className="text-xs text-slate-400">Last updated: 28 March 2026</p>
              <div className="flex gap-3">
                <Link to="/privacy-policy" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Privacy Policy</Link>
                <span className="text-slate-300">·</span>
                <Link to="/terms-of-use" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Terms of Use</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
